import Icon from "../components/ui/Icon";
import { Button } from "../components/ui/Button";
import { useQuery } from "@tanstack/react-query";
import { getNewsApi } from "../api/resources.api";
import EmptyState from "../components/EmptyState";
import { getImageSrc } from "../utils/getImageSrc";
import LazyImage from "../components/ui/LazyImage";
import NewsSection from "../components/NewsSection";
import MediaViewer from "../components/MediaViewer";
import { useState, useEffect, useRef } from "react";
import CategoryIcon from "../components/CatIconMapper";
import { toPersianDate } from "../utils/dateFormatter";
import greenLogo from "../assets/images/green-logo.png";
import { isEmptyContent } from "../utils/isEmptyContent";
import ProjectGallery from "../components/ProjectGallery";
import { useSearch } from "../features/header/hooks/useSearch";
import { useSingleNews } from "../features/news/hook/useSingleNews";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useSingleProject } from "../features/home/hooks/useSingleProject";
import { useFollowCheck } from "../features/managers/hooks/useFollowCheck";
import { useFollowProject } from "../features/managers/hooks/useFollowProject";
import { useToggleNewsFollow } from "../features/news/hook/useToggleNewsFollow";
import {
  getSearchResultData,
  normalizeSearchNewsItems,
} from "../utils/normalizeSearchNews";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

export default function NewsDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const newsId = id ? Number(id) : undefined;
  const isListPage = !id;
  const searchQuery = searchParams.get("q")?.trim() ?? "";
  const isSearchListPage = isListPage && searchQuery.length > 0;
  const [newsPage, setNewsPage] = useState(1);
  const lastScrollY = useRef(0);
  const [showSticky, setShowSticky] = useState(false);
  const scrollUpDistance = useRef(0);
  const [newsItems, setNewsItems] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeListSlides, setActiveListSlides] = useState<
    Record<number, number>
  >({});
  const { data: newsListResponse, isLoading: listLoading } = useQuery({
    queryKey: ["news", "list", newsPage],
    queryFn: async () => {
      const response = await getNewsApi({ page: newsPage });
      return response as {
        status: boolean;
        data: any[];
        meta?: {
          current_page: number;
          last_page: number;
          per_page: number;
          total: number;
          has_more_pages?: boolean;
          next_page_url?: string | null;
          prev_page_url?: string | null;
        };
      };
    },
    enabled: isListPage && !isSearchListPage,
    staleTime: 1000 * 60 * 2,
  });
  const {
    data: searchResponse,
    isLoading: searchLoading,
    error: searchError,
  } = useSearch(searchQuery);
  const searchNewsItems = normalizeSearchNewsItems(
    getSearchResultData(searchResponse?.results?.news).length > 0
      ? getSearchResultData(searchResponse?.results?.news)
      : getSearchResultData(searchResponse?.results?.items),
  );
  const newsMeta = newsListResponse?.meta;
  const hasMoreNewsPages = newsMeta
    ? (newsMeta.has_more_pages ?? newsMeta.current_page < newsMeta.last_page)
    : false;
  const { data: newsResponse, isLoading } = useSingleNews(newsId);
  const { mutate, isPending } = useToggleNewsFollow();
  const newsItem = newsResponse?.data;
  const newsDate = newsItem?.release_date ?? newsItem?.date_release ?? null;
  const [isSaved, setIsSaved] = useState(newsItem?.is_followed ?? false);
  const projectId = newsItem?.projects?.[0]?.id;
  const { data: singleProject } = useSingleProject(projectId);
  const mainPersons = singleProject?.persons ?? [];
  const projectPerson = singleProject?.persons?.[0];
  const { toggleFollowProject, loading: followLoading } = useFollowProject();
  const { data: checkedProjectFollowing, isFetching: projectCheckLoading } =
    useFollowCheck("projects", projectId);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [activeMainSlide, setActiveMainSlide] = useState(0);
  const projectParam = searchParams.get("project");
  const queryProjectId = projectParam ? Number(projectParam) : undefined;
  const sidebarProjectId =
    queryProjectId && !Number.isNaN(queryProjectId)
      ? queryProjectId
      : newsItem?.projects?.[0]?.id;

  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1280;

      if (!isMobile) {
        setShowSticky(false);
        lastScrollY.current = 0;
        scrollUpDistance.current = 0;
        return;
      }

      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      const goingDown = diff > 0;
      const goingUp = diff < 0;

      // ───────── SHOW ─────────
      if (goingDown && currentY > 120) {
        setShowSticky(true);
        scrollUpDistance.current = 0; // reset
      }

      // ───────── HIDE (FIX اصلی اینجاست) ─────────
      if (goingUp) {
        scrollUpDistance.current += Math.abs(diff);
        if (scrollUpDistance.current > 60) {
          setShowSticky(false);
        }
      } else {
        scrollUpDistance.current = 0;
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isListPage || !newsListResponse?.data) return;

    setNewsItems((prev) =>
      newsPage === 1
        ? newsListResponse.data
        : [...prev, ...newsListResponse.data],
    );
    setIsLoadingMore(false);
  }, [isListPage, newsListResponse?.data, newsPage]);

  useEffect(() => {
    if (typeof checkedProjectFollowing === "boolean") {
      setIsFollowing(checkedProjectFollowing);
    }
  }, [checkedProjectFollowing]);

  const handleToggleFollowProject = async () => {
    if (!projectId) return;
    const res = await toggleFollowProject(projectId);
    if (res) setIsFollowing(res.isFollowing);
  };

  const handleToggleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newsItem) return;
    mutate(newsItem.id, {
      onSuccess: () => {
        setIsSaved((prev: any) => !prev);
      },
    });
  };

  if (isListPage) {
    const visibleNewsItems = isSearchListPage ? searchNewsItems : newsItems;
    const isInitialLoading = isSearchListPage
      ? searchLoading
      : listLoading && newsItems.length === 0;
    const visibleTotal = isSearchListPage
      ? (searchResponse?.results?.news?.total ??
        searchResponse?.results?.items?.total ??
        visibleNewsItems.length)
      : (newsMeta?.total ?? newsItems.length);

    return (
      <div className="w-full min-h-screen bg-green-01 pb-10">
        <div className="px-4 xl:px-11 my-14 space-y-5">
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-1">
              <h1 className="text-lg xl:text-2xl font-bold text-green-06">
                {isSearchListPage
                  ? `نتایج خبر برای: "${searchQuery}"`
                  : "همه خبرها"}
              </h1>
            </div>
            <div className="text-xs text-green-04">
              {isInitialLoading ? "در حال دریافت..." : `${visibleTotal} خبر`}
            </div>
          </div>

          {isInitialLoading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : searchError ? (
            <div className="flex items-center justify-center min-h-[40vh] text-red-500">
              خطا در دریافت نتایج خبر
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 xl:px-4"
                dir="rtl"
              >
                {visibleNewsItems?.map((item: any) => {
                  const hasImages =
                    Array.isArray(item.images) && item.images.length > 0;
                  const activeIndex = activeListSlides[item.id] ?? 0;
                  const cover =
                    getImageSrc(
                      item.images,
                      item.image,
                      item.cover,
                      item.logo,
                      item.projects?.[0]?.logo,
                      item.extra,
                    ) || greenLogo;

                  return (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="flex flex-col bg-white hover:bg-white/70 transition shadow-sm h-[362px] p-[15px] xl:p-4 overflow-hidden rounded-[20px]"
                    >
                      <div className="flex w-full h-11 justify-between items-center gap-x-2.5">
                        <div className="flex items-start gap-x-1 xl:gap-x-2.5 min-w-0">
                          <div className="flex gap-x-2.5 items-center min-w-0">
                            {item.categories?.length > 0 ? (
                              item.categories.map((category: any) => (
                                <CategoryIcon
                                  key={`news-${item.id}-icon-${category.id}`}
                                  id={category.id}
                                  className="size-5 xl:size-6 text-green-04 shrink-0"
                                />
                              ))
                            ) : (
                              <CategoryIcon className="size-5 xl:size-6 text-green-05 shrink-0" />
                            )}
                            <div className="min-w-0">
                              {item.categories?.map((cat: any) => (
                                <span
                                  key={`news-${item.id}-cat-${cat.id}`}
                                  className="font-estedad line-clamp-1 text-[11px] xl:text-xs font-semibold text-green-04 block truncate"
                                >
                                  {cat.name}
                                </span>
                              ))}

                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="text-[11px] xl:text-xs font-normal font-estedad text-[#98B1AF] line-clamp-1">
                                      {item.projects?.[0]?.name || "خبر"}
                                    </p>
                                  </TooltipTrigger>

                                  <TooltipContent className="font-estedad bg-green-02.5 text-green-05">
                                    <p>{item.projects?.[0]?.name || "خبر"}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-x-3 shrink-0">
                          {hasImages && (
                            <div className="flex items-center border border-green-04 h-5 px-1 gap-x-1.5 rounded-md">
                              <span className="flex flex-row-reverse items-center text-xs font-medium text-green-04 pt-1">
                                <p className="font-bold">{activeIndex + 1}</p>
                                <span className="font-light">
                                  {item.images.length}/
                                </span>
                              </span>
                              <Icon
                                icon="Camera"
                                className="w-4 text-green-04"
                              />
                            </div>
                          )}
                          <Icon
                            icon="Save"
                            className="size-4 xl:size-[18px] text-green-04 transition"
                          />
                        </div>
                      </div>

                      <div
                        className="relative bg-green-01 h-[182px] overflow-hidden mt-1 rounded-[16px]"
                        onClick={(e) => {
                          if (hasImages) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                      >
                        {hasImages ? (
                          <MediaViewer
                            form="form2"
                            images={item.images}
                            activeIndex={activeIndex}
                            onChange={(index) =>
                              setActiveListSlides((prev) => ({
                                ...prev,
                                [item.id]: index,
                              }))
                            }
                            forceSlider={true}
                          />
                        ) : (
                          <LazyImage
                            src={cover}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      <span className="h-3 text-xs font-normal text-accent-04/80 mt-3">
                        {item.release_date ?? item.date_release ?? ""}
                      </span>

                      <div
                        className="estedad line-clamp-2 mt-1"
                        dangerouslySetInnerHTML={{ __html: item.name || "" }}
                      />

                      <div
                        className="estedad3 news-content line-clamp-1 pt-1 overflow-hidden break-words max-w-full"
                        dangerouslySetInnerHTML={{
                          __html:
                            (typeof item.subtitle === "string" &&
                              item.subtitle) ||
                            (typeof item.meta_text === "string" &&
                              item.meta_text) ||
                            "",
                        }}
                      />
                    </Link>
                  );
                })}
              </div>

              {!visibleNewsItems?.length && (
                <div className="col-span-full text-center py-20 text-green-04">
                  خبری برای نمایش وجود ندارد.
                </div>
              )}
              {!isSearchListPage && (
                <div className="w-full flex items-center justify-center xl:mt-14 gap-3">
                  <button
                    type="button"
                    disabled={!hasMoreNewsPages}
                    onClick={() => {
                      if (!hasMoreNewsPages || isLoadingMore) return;
                      setIsLoadingMore(true);
                      setNewsPage((prev) => prev + 1);
                    }}
                    className="text-[11px] font-medium bg-green-02 text-complement-06 font-estedad px-4 py-1.5 rounded-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isLoadingMore ? (
                      <span className="flex items-center gap-2">
                        <span className="size-3 border border-current border-t-transparent rounded-full animate-spin" />
                      </span>
                    ) : (
                      "بیشتر"
                    )}
                  </button>
                  <span className="text-[11px] text-green-04">
                    صفحه {newsMeta?.current_page ?? newsPage} از{" "}
                    {newsMeta?.last_page ?? "-"}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-green-01">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!newsItem)
    return <div className="text-center py-20">خبر مورد نظر یافت نشد.</div>;

  return (
    <div className="w-full min-h-screen bg-green-01 pb-10">
      <div className="flex flex-col xl:flex-row-reverse gap-4 items-start px-4 xl:px-11">
        {/* aside left */}
        <aside className="hidden xl:block w-full xl:w-[27%] 2xl:w-[22%] space-y-4 xl:max-h-[1580px] no-scrollbar pb-1.5 xl:overflow-y-auto">
          <NewsSection
            form="form1"
            projectId={sidebarProjectId}
            excludeId={Number(id)}
            showHeader={false}
            noCardMargin
            showMedia={true}
            categoryOverride={
              !newsItem.categories || newsItem.categories.length === 0
                ? singleProject?.categories
                : undefined
            }
          />
        </aside>
        {/* main */}
        <main className="w-full xl:w-[58%] 2xl:w-[59%] space-y-4">
          {newsItem.projects?.[0] && (
            <div
              className={`fixed top-0 left-0 right-0 z-30 transition-all duration-150 ease-in-out
            ${
              showSticky
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-10 pointer-events-none"
            }`}
            >
              <div className="w-full bg-green-01/90 backdrop-blur-xl px-2.5 h-[72px] flex items-center justify-between pl-8 gap-3 shadow-sm border-b border-green-02/40">
                <Link
                  to={`/institutions/${newsItem.projects?.[0]?.id}`}
                  className="flex items-center gap-x-2.5 min-w-0"
                >
                  <LazyImage
                    src={singleProject?.logo || greenLogo}
                    alt="لوگوی مجموعه"
                    className="size-10 border border-green-02 object-cover shrink-0 bg-white rounded-xl"
                  />

                  <h3 className="text-[9px] line-clamp-2 text-wrap font-bold text-green-05 truncate">
                    {newsItem.projects?.[0]?.name || ""}
                  </h3>
                </Link>
                <Link
                  to={`/managers/${projectPerson?.id}`}
                  className="flex items-center justify-center gap-x-2.5"
                >
                  <LazyImage
                    src={projectPerson?.logo || greenLogo}
                    alt="تصویر مدیر"
                    onError={(e) => {
                      e.currentTarget.src = greenLogo;
                    }}
                    className="size-10 rounded-full border border-green-02 object-cover shrink-0 bg-white"
                  />
                  <div className="space-y-0.5">
                    <h2 className="text-[9px] text-green-04 font-medium leading-[11px]">
                      {projectPerson?.nickname}
                    </h2>
                    <h2 className="text-xs font-bold text-green-05 truncate">
                      {projectPerson?.name}
                    </h2>
                  </div>
                </Link>
              </div>
            </div>
          )}

          <article className="xl:bg-white overflow-hidden xl:shadow-sm xl:p-7 xl:!m-0 xl:rounded-[25px]">
            {newsItem.projects?.[0] && (
              <div className="xl:hidden flex flex-col items-start justify-start bg-white p-3 shadow-sm rounded-[20px] overflow-hidden">
                {/* project */}
                <div className="flex items-center gap-x-2.5 w-full">
                  <LazyImage
                    src={singleProject?.logo || greenLogo}
                    alt={singleProject?.name || newsItem.projects?.[0]?.name}
                    className="size-[70px] border border-green-02 object-cover rounded-[20px] shrink-0"
                  />
                  <Link
                    to={`/institutions/${newsItem.projects?.[0]?.id}`}
                    className="min-w-0"
                  >
                    <div className="flex flex-col justify-center gap-y-0.5">
                      {singleProject?.categories?.[0]?.name && (
                        <span className="text-[11px] text-green-04 font-normal">
                          {singleProject.categories[0].name}
                        </span>
                      )}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="line-clamp-1 text-[15px] font-bold text-green-05">
                              {newsItem.projects?.[0]?.name}
                            </div>
                          </TooltipTrigger>

                          <TooltipContent className="bg-green-02.5 text-green-05">
                            <p>{newsItem.projects?.[0]?.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {singleProject?.date_establishment && (
                        <span className="text-[11px] text-green-04 font-normal">
                          {toPersianDate(singleProject.date_establishment)}
                        </span>
                      )}
                    </div>
                  </Link>
                </div>

                {/* person project*/}
                <div className="flex items-center gap-x-2 w-full mt-2.5 bg-green-02/50 p-2 rounded-2xl">
                  <LazyImage
                    src={projectPerson?.logo || greenLogo}
                    alt={projectPerson?.name || ""}
                    className="size-11 object-cover !border-[1px] border-green-0 shrink-0 rounded-[10px]"
                  />
                  <Link
                    to={`/managers/${projectPerson?.id}`}
                    className="min-w-0 space-y-1"
                  >
                    <p className="text-[10px] font-medium text-green-04/80">
                      {projectPerson?.nickname}
                    </p>
                    <p className="text-xs font-bold text-green-05 truncate">
                      {projectPerson?.name}
                    </p>
                  </Link>
                </div>

                {/*Follow Box*/}
                <Button
                  onClick={handleToggleFollowProject}
                  disabled={followLoading || projectCheckLoading}
                  className={`w-full flex items-center justify-center gap-2 text-sm font-medium mt-2.5 rounded-xl border-none transition-all
                 ${
                   isFollowing
                     ? "bg-green-02 text-green-04 hover:bg-green-02/70 hover:text-green-04/90"
                     : "text-white bg-green-05"
                 }`}
                >
                  {followLoading || projectCheckLoading ? (
                    <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isFollowing ? (
                    "دنبال میکنید"
                  ) : (
                    "دنبال کردن"
                  )}
                </Button>
              </div>
            )}

            {(newsItem.projects?.length > 0 ||
              newsItem.categories?.length > 0) && (
              <div className="xl:hidden flex w-full items-center justify-between gap-x-1 xl:gap-x-2.5 mt-5">
                <div className="flex flex-col items-center justify-center">
                  {newsItem.projects?.map((p) => (
                    <div key={p.id} className="flex gap-x-2.5 items-center">
                      {singleProject?.categories?.map((cat: any) => (
                        <div
                          key={cat.id}
                          className="flex items-center gap-x-2.5 text-[11px] xl:text-xs font-normal text-green-04/80"
                        >
                          <CategoryIcon
                            id={cat.id}
                            className="size-5 text-green-04"
                          />
                          <div className="leading-4 line-clamp-2">
                            <span>{cat.name}</span>
                            <p className="text-[11px] xl:text-xs font-semibold text-green-04">
                              {p.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                {/* Left actions */}
                <button
                  onClick={handleToggleSave}
                  disabled={isPending}
                  className="flex items-center justify-center"
                >
                  <Icon
                    icon={isSaved ? "Saved" : "Save"}
                    className={`size-4 xl:size-[18px] transition ${
                      isSaved ? "text-green-05" : "text-green-04"
                    }`}
                  />
                </button>
              </div>
            )}
            <div className="hidden xl:flex items-center justify-start w-full gap-x-2.5 text-[14px]">
              <div className="flex justify-between items-center w-full">
                <span className="text-sm bg-accent-01 text-accent-05 px-2.5 py-1 font-semibold rounded-lg">
                  خبــر{" "}
                </span>

                <button
                  onClick={handleToggleSave}
                  disabled={isPending}
                  className="flex items-center justify-center bg-green-01 p-2 rounded-xl"
                >
                  <Icon
                    icon={isSaved ? "Saved" : "Save"}
                    className={`size-4 xl:size-[18px] transition ${
                      isSaved ? "text-green-05" : "text-green-04"
                    }`}
                  />
                </button>
              </div>
            </div>
            <h1 className="hidden xl:block text-xl xl:text-base font-semibold text-green-06 leading-relaxed mt-1">
              {newsItem.name}
            </h1>
            <p className="text-xs font-normal text-accent-03 mt-1">
              {newsDate ? ` ${newsDate}` : ""}
            </p>
            <p className="text-sm text-green-05 mt-2 leading-6">
              {newsItem.subtitle || newsItem.meta_text}
            </p>
            {newsItem.images && newsItem.images.length > 0 && (
              <div className="flex flex-col w-full mt-2.5 xl:mt-5 rounded-2xl xl:rounded-[20px] overflow-hidden h-[201px] xl:h-[379px]">
                <MediaViewer
                  form="form2"
                  images={newsItem.images}
                  activeIndex={activeMainSlide}
                  onChange={setActiveMainSlide}
                  forceSlider={true}
                  enableMobileViewer
                />
              </div>
            )}

            <div className="mt-5" dir="rtl">
              <p className="xl:hidden text-xs font-normal text-accent-03">
                {newsDate ? ` ${newsDate}` : ""}
              </p>
              <h1 className="xl:hidden text-sm font-bold text-green-06 leading-relaxed">
                {newsItem.name}
              </h1>
              {isEmptyContent(newsItem.content) ? (
                <EmptyState />
              ) : (
                <div
                  className="estedad2"
                  dangerouslySetInnerHTML={{ __html: newsItem.content || "" }}
                />
              )}

              {newsItem.tags && newsItem.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-8 xl:pt-6 xl:border-t border-gray-100">
                  {newsItem.tags.map((tag: any, idx: number) => (
                    <span
                      key={idx}
                      className="bg-green-01 text-green-06 px-3 py-1 rounded-md text-xs"
                    >
                      #{tag?.name || tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </article>
        </main>
        {/* aside right */}
        <aside className="w-full xl:w-[25%] 2xl:w-[19%] space-y-4">
          {newsItem.projects?.[0] && (
            <div className="hidden xl:flex flex-col items-start justify-start bg-white p-3 shadow-sm rounded-[20px] overflow-hidden">
              {/* project */}
              <div className="flex items-center gap-x-2.5 w-full">
                <LazyImage
                  src={singleProject?.logo || greenLogo}
                  className="size-[70px] border border-green-02 object-cover rounded-[20px] shrink-0"
                  alt={singleProject?.name || newsItem.projects?.[0]?.name}
                />
                <Link
                  to={`/institutions/${newsItem.projects?.[0]?.id}`}
                  className="min-w-0"
                >
                  <div className="flex flex-col justify-center gap-y-0.5">
                    {singleProject?.categories?.[0]?.name && (
                      <span className="text-[11px] text-green-04 font-normal">
                        {singleProject.categories[0].name}
                      </span>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="line-clamp-1 text-[15px] font-bold text-green-05">
                            {newsItem.projects?.[0]?.name}
                          </div>
                        </TooltipTrigger>

                        <TooltipContent className="bg-green-02.5 text-green-05">
                          <p>{newsItem.projects?.[0]?.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {singleProject?.date_establishment && (
                      <span className="text-[11px] text-green-04 font-normal">
                        {toPersianDate(singleProject.date_establishment)}
                      </span>
                    )}
                  </div>
                </Link>
              </div>

              {/* person project*/}
              <div className="flex items-center gap-x-2 w-full mt-2.5 bg-green-02/50 p-2 rounded-2xl">
                <LazyImage
                  src={projectPerson?.logo || greenLogo}
                  alt={projectPerson?.name || ""}
                  className="size-11 object-cover !border-[1px] border-green-0 shrink-0 rounded-[10px]"
                />
                <Link
                  to={`/managers/${projectPerson?.id}`}
                  className="min-w-0 space-y-1"
                >
                  <p className="text-[10px] font-medium text-green-04/80">
                    {projectPerson?.nickname}
                  </p>
                  <p className="text-xs font-bold text-green-05 truncate">
                    {projectPerson?.name}
                  </p>
                </Link>
              </div>

              {/*Follow Box*/}
              <Button
                onClick={handleToggleFollowProject}
                disabled={followLoading || projectCheckLoading}
                className={`w-full flex items-center justify-center gap-2 text-sm font-medium mt-2.5 rounded-xl border-none transition-all
                 ${
                   isFollowing
                     ? "bg-green-02 text-green-04 hover:bg-green-02/70 hover:text-green-04/90"
                     : "text-white bg-green-05"
                 }`}
              >
                {followLoading || projectCheckLoading ? (
                  <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : isFollowing ? (
                  "دنبال میکنید"
                ) : (
                  "دنبال کردن"
                )}
              </Button>
            </div>
          )}

          {/* Members */}
          <div className="bg-white p-2.5 shadow-sm rounded-[20px]">
            <h4 className="text-base font-semibold text-accent-05">حاضران</h4>

            <div className="flex gap-2 mt-2.5">
              {mainPersons?.length > 0 && (
                <>
                  {mainPersons.map((p, i) => (
                    <Link
                      key={p.id ?? i}
                      to={`/managers/${p.id}`}
                      title={p.name}
                      className="block"
                    >
                      <LazyImage
                        src={p.logo || greenLogo}
                        alt={p.name || ""}
                        className="max-w-[45px] aspect-square hover:scale-[1.02] transition object-cover border border-green-04/50 rounded-full cursor-pointer"
                      />
                    </Link>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Multimedia */}
          <div className="hidden xl:block">
            <ProjectGallery images={newsItem.images} />
          </div>
        </aside>
      </div>
    </div>
  );
}
